/**
 * DamSuggestionPanel — surfaces "typical for {archetype}" suggestions
 * pulled from the Datacrate Model (Configuration Upgrade CU6).
 *
 * When the site has a `companyArchetype` set AND the DAM for the
 * current report type emits `defaultPrefsByArchetype[archetype]`, the
 * panel renders one SuggestionChip per field whose current value is
 * undefined. Clicking the chip merges the suggested value into the
 * current prefs.
 *
 * Stub-tolerant: when the DAM hasn't yet been rebuilt with
 * defaultPrefsByArchetype (older builds — most of them today), the
 * panel renders nothing. CU6 ships ahead of the DAM template emit;
 * suggestions populate later as DAM builds catch up.
 *
 * Calls the suggestion an IP-style chip even though the IP pack
 * authors its own SuggestionChip — this file ships a thin local one
 * so CU6 doesn't block on IP's order.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import type { ReportTypeId } from '../../../config/reportTypes';
import type { TypedPreferencesByType } from '../../../types/siteRag';
import type { DatacrateModel } from '../../../types/datacrateModel';
import type { CompanyArchetype } from '../../../types/company';
import { COMPANY_ARCHETYPES } from '../../../types/company';
import { labelFor } from '../../../lib/reportPreferences/labels';

/**
 * Local SuggestionChip — thin reusable affordance for "AI-derived
 * suggestion, click to apply". The IP pack ships its own variant; this
 * local copy avoids a cross-pack dependency. Wraps a value display +
 * a clickable Apply CTA.
 */
export interface SuggestionChipProps {
  /** Field key (for test reachability). */
  fieldKey: string;
  /** Suggested value — rendered as its string form. */
  value: unknown;
  /** Human-readable rationale shown beneath the value. */
  rationale: string;
  /** Confidence percentage 0–100. */
  confidence?: number;
  /** Provenance — 'dam-default', 'doc-ai', etc. Future hook. */
  source?: string;
  onAccept: () => void;
}

export const SuggestionChip: React.FC<SuggestionChipProps> = ({
  fieldKey,
  value,
  rationale,
  confidence,
  source: _source,
  onAccept,
}) => (
  <div
    className="flex items-start gap-2 type-body rounded-lg border border-primary/30 bg-primary/5 px-2 py-1.5"
    data-testid={`suggestion-chip-${fieldKey}`}
  >
    <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
    <div className="flex-1 leading-snug">
      <div className="flex items-center gap-2">
        <span className="font-medium text-text-primary">
          Suggested: {formatSuggestedValue(value)}
        </span>
        {typeof confidence === 'number' && (
          <span className="text-text-muted">{confidence}%</span>
        )}
      </div>
      <div className="text-text-muted">{rationale}</div>
      <button
        type="button"
        onClick={onAccept}
        className="mt-1 inline-flex items-center type-meta uppercase tracking-widest text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        data-testid={`suggestion-accept-${fieldKey}`}
      >
        Apply
      </button>
    </div>
  </div>
);

function formatSuggestedValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

/* ── Panel ──────────────────────────────────────────────────── */

export interface DamSuggestionPanelProps<T extends ReportTypeId> {
  reportType: T;
  /** Current edited prefs. Used to filter out fields the user already set. */
  value: NonNullable<TypedPreferencesByType[T]>;
  /** Resolved archetype for the site/company. */
  archetype?: CompanyArchetype;
  /** DAM for the current report type — may lack defaultPrefsByArchetype. */
  dam?: DatacrateModel;
  /** Merge a single field value into the current prefs. */
  onAccept: (fieldKey: string, value: unknown) => void;
}

export function DamSuggestionPanel<T extends ReportTypeId>({
  reportType,
  value,
  archetype,
  dam,
  onAccept,
}: DamSuggestionPanelProps<T>) {
  // CU6 — stub fallback when DAM doesn't yet emit defaultPrefsByArchetype.
  if (!archetype || !dam?.defaultPrefsByArchetype) return null;
  const map = dam.defaultPrefsByArchetype[archetype];
  if (!map) return null;
  const meta = COMPANY_ARCHETYPES[archetype];
  const archetypeLabel = meta?.label ?? archetype;
  const prefs = value as unknown as Record<string, unknown>;

  // Only suggest fields the user hasn't already set.
  const entries = Object.entries(map).filter(
    ([k]) => prefs[k] === undefined,
  );

  if (entries.length === 0) return null;

  return (
    <div
      className="border border-primary/30 bg-primary/5 p-2 space-y-1.5"
      data-testid="dam-suggestion-panel"
    >
      <div className="type-body font-bold uppercase tracking-widest text-primary">
        Suggested defaults for {archetypeLabel}
      </div>
      <div className="type-meta text-text-muted">
        From the Datacrate Model for {reportType}.
      </div>
      <div className="space-y-1.5">
        {entries.map(([fieldKey, suggestedValue]) => (
          <SuggestionChip
            key={fieldKey}
            fieldKey={fieldKey}
            value={suggestedValue}
            rationale={`Typical ${labelFor(reportType, fieldKey)} for ${archetypeLabel} per DAM defaults`}
            confidence={70}
            source="dam-default"
            onAccept={() => onAccept(fieldKey, suggestedValue)}
          />
        ))}
      </div>
    </div>
  );
}
